import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import ImageDropUpload from "../components/ImageDropUpload";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Gem, Plus, Save, Trash2, X } from "lucide-react";

const BEAD_SIZES = [6, 8, 10, 12, 14];

const emptyCrystal = (sortOrder) => ({
    name: "",
    image_url: "",
    effect: "",
    color: "#9B59B6",
    compatible_sizes: [],
    enabled: true,
    sort_order: sortOrder,
});

export default function CrystalsPage() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(emptyCrystal(0));
    const [editing, setEditing] = useState("");
    const [saving, setSaving] = useState(false);

    const sorted = useMemo(
        () => [...items].sort((a, b) => a.sort_order - b.sort_order),
        [items],
    );

    const load = async () => {
        const { data } = await api.get("/crystals");
        setItems(data);
        return data;
    };

    useEffect(() => {
        load().catch(() => toast.error("水晶列表加载失败"));
    }, []);

    const startNew = () => {
        setEditing("");
        setForm(emptyCrystal(items.length));
    };

    const startEdit = (c) => {
        setEditing(c.id);
        setForm({
            name: c.name || "",
            image_url: c.image_url || "",
            effect: c.effect || "",
            color: c.color || "#9B59B6",
            compatible_sizes: c.compatible_sizes || [],
            enabled: c.enabled !== false,
            sort_order: c.sort_order || 0,
        });
    };

    const toggleSize = (size) => {
        const has = form.compatible_sizes.includes(size);
        setForm({
            ...form,
            compatible_sizes: has
                ? form.compatible_sizes.filter((s) => s !== size)
                : [...form.compatible_sizes, size].sort((a, b) => a - b),
        });
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("请填写水晶名称");
        setSaving(true);
        try {
            if (editing) await api.put(`/crystals/${editing}`, form);
            else await api.post("/crystals", form);
            const fresh = await load();
            toast.success("水晶已保存");
            setEditing("");
            setForm(emptyCrystal(fresh.length));
        } catch (e) {
            toast.error(e.response?.data?.detail || "保存失败");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`确定删除「${c.name}」？`)) return;
        try {
            await api.delete(`/crystals/${c.id}`);
            await load();
            if (editing === c.id) startNew();
            toast.success("已删除");
        } catch (e) {
            toast.error(e.response?.data?.detail || "删除失败");
        }
    };

    const toggleEnabled = async (c) => {
        try {
            await api.put(`/crystals/${c.id}`, { ...c, enabled: !(c.enabled !== false) });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "更新失败");
        }
    };

    const move = async (index, delta) => {
        const next = [...sorted];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        const [moved] = next.splice(index, 1);
        next.splice(target, 0, moved);
        setItems(next.map((item, i) => ({ ...item, sort_order: i })));
        try {
            await api.put("/crystals/sort-order", {
                items: next.map((item, i) => ({ id: item.id, sort_order: i })),
            });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "排序失败");
            await load();
        }
    };

    return (
        <div className="space-y-6" data-testid="crystals-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">落地页</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1 flex items-center gap-3">
                        <Gem className="h-7 w-7 text-gold-600" /> 水晶管理
                    </h1>
                    <p className="text-sm text-gold-500 mt-1">
                        供落地页「手链 DIY 设计器」使用 · 停用的水晶不会对外显示
                    </p>
                </div>
                <Button data-testid="crystal-new-btn" onClick={startNew} className="h-10 bg-gold-600 hover:bg-gold-700 text-white gap-2">
                    <Plus className="h-4 w-4" /> 新增水晶
                </Button>
            </div>

            <Card className="p-6 border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">
                        {editing ? "编辑水晶" : "新增水晶"}
                    </h2>
                    {editing && (
                        <Button variant="ghost" size="sm" onClick={startNew} className="text-slate-500 gap-1">
                            <X className="h-3.5 w-3.5" /> 取消编辑
                        </Button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label>名称 *</Label>
                        <Input
                            data-testid="crystal-name-input"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="紫水晶"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>功效简介（20 字内）</Label>
                        <Input
                            data-testid="crystal-effect-input"
                            value={form.effect}
                            maxLength={20}
                            onChange={(e) => setForm({ ...form, effect: e.target.value })}
                            placeholder="开智慧・平静心绪"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>颜色（无图片时落地页用色块代替）</Label>
                        <div className="flex items-center gap-2">
                            <input
                                data-testid="crystal-color-picker"
                                type="color"
                                value={form.color}
                                onChange={(e) => setForm({ ...form, color: e.target.value })}
                                className="h-10 w-14 rounded border border-slate-200 bg-white p-1 cursor-pointer"
                            />
                            <Input
                                data-testid="crystal-color-input"
                                value={form.color}
                                onChange={(e) => setForm({ ...form, color: e.target.value })}
                                className="font-mono"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>适用珠径（mm · 不选=全部适用）</Label>
                        <div className="flex flex-wrap gap-2 h-10 items-center">
                            {BEAD_SIZES.map((size) => {
                                const active = form.compatible_sizes.includes(size);
                                return (
                                    <button
                                        key={size}
                                        type="button"
                                        data-testid={`crystal-size-${size}`}
                                        onClick={() => toggleSize(size)}
                                        className={`px-3 h-8 rounded-full text-xs font-medium border transition-colors ${
                                            active
                                                ? "bg-gold-600 text-white border-gold-600"
                                                : "bg-white text-slate-600 border-slate-300 hover:border-gold-400"
                                        }`}
                                    >
                                        {size}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                        <Label>珠子图片（正方形，建议 ≥ 200×200）</Label>
                        <ImageDropUpload
                            value={form.image_url}
                            onChange={(v) => setForm({ ...form, image_url: v || "" })}
                            maxEdge={600}
                            uploadToServer
                            previewClassName="aspect-square max-w-[160px]"
                            testIdPrefix="crystal-image"
                            helperText="JPG / PNG · 自动压缩后上传并生成链接"
                        />
                    </div>
                    <div className="flex items-center gap-3 md:col-span-2">
                        <Switch
                            data-testid="crystal-enabled-switch"
                            checked={form.enabled}
                            onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                        />
                        <span className="text-sm text-slate-700">{form.enabled ? "已启用（对外显示）" : "已停用"}</span>
                    </div>
                </div>

                <Button data-testid="crystal-save-btn" onClick={save} disabled={saving} className="h-10 bg-gold-600 hover:bg-gold-700 text-white gap-2">
                    <Save className="h-4 w-4" /> {saving ? "保存中..." : editing ? "保存修改" : "新增水晶"}
                </Button>
            </Card>

            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">水晶列表</h2>
                    <Badge className="bg-gold-100 text-gold-800 border-0" data-testid="crystal-count-badge">
                        共 {sorted.length} 种
                    </Badge>
                </div>
                {sorted.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-gold-500">还没有水晶，先在上方新增一颗吧</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {sorted.map((c, index) => (
                            <div key={c.id} data-testid={`crystal-row-${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-gold-50/60">
                                <div className="flex flex-col gap-0.5">
                                    <button
                                        type="button"
                                        data-testid={`crystal-up-${c.id}`}
                                        onClick={() => move(index, -1)}
                                        disabled={index === 0}
                                        className="h-5 w-5 rounded flex items-center justify-center text-slate-400 hover:bg-white disabled:opacity-30"
                                    >
                                        <ArrowUp className="h-3 w-3" />
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`crystal-down-${c.id}`}
                                        onClick={() => move(index, 1)}
                                        disabled={index === sorted.length - 1}
                                        className="h-5 w-5 rounded flex items-center justify-center text-slate-400 hover:bg-white disabled:opacity-30"
                                    >
                                        <ArrowDown className="h-3 w-3" />
                                    </button>
                                </div>
                                {c.image_url ? (
                                    <img src={c.image_url} alt={c.name} className="h-10 w-10 rounded-full object-cover border border-slate-200" />
                                ) : (
                                    <div className="h-10 w-10 rounded-full border border-slate-200" style={{ background: c.color }} />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                                    <div className="text-xs text-slate-500 truncate">
                                        {c.effect || "—"} · 珠径 {(c.compatible_sizes || []).length ? c.compatible_sizes.join("/") + "mm" : "全部"}
                                    </div>
                                </div>
                                <span className="hidden sm:inline text-xs font-mono text-slate-400">{c.color}</span>
                                <Switch
                                    data-testid={`crystal-toggle-${c.id}`}
                                    checked={c.enabled !== false}
                                    onCheckedChange={() => toggleEnabled(c)}
                                />
                                <Button variant="ghost" size="sm" data-testid={`crystal-edit-${c.id}`} onClick={() => startEdit(c)} className="text-slate-600 hover:text-gold-700">
                                    编辑
                                </Button>
                                <Button variant="ghost" size="sm" data-testid={`crystal-delete-${c.id}`} onClick={() => remove(c)} className="text-red-600 hover:bg-red-50">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Card className="p-4 border-dashed border-gold-300 bg-gold-50/40">
                <div className="text-xs font-semibold uppercase tracking-wider text-gold-700 mb-2">开发者接口</div>
                <div className="text-xs font-mono text-slate-700 space-y-1">
                    <div>公开读取：<code className="bg-white/80 px-1.5 py-0.5 rounded border border-gold-200">GET /api/public/crystals</code>（仅启用项，按排序升序，无数据返回 []）</div>
                    <div>设计回传：<code className="bg-white/80 px-1.5 py-0.5 rounded border border-gold-200">POST /api/public/bracelet-design</code></div>
                </div>
            </Card>
        </div>
    );
}
